//================================================================
//  Component: Execute Tab
//================================================================

//  Purpose: This contains the execute information for a commercial task

//  Properties:
//    - task = {An object, contains the commercial task details}

//  Example:
//    <ExecuteTab
//      task={task}
//    ></ExecuteTab>    

// ==============================================================

// Libraries
import React, { useContext, useReducer, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';

// Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

// Components
import Tooltip from '../../../../Components/Tooltip/Tooltip';
import AddContractPane from './Panes/AddContractPane';
import ViewContractPane from './Panes/ViewContractPane';
import DeleteContractModal from './Modals/DeleteContractModal';

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import ConvertDate from '../../../../Library/ConvertDate';
import QueryListener from '../../../../Library/QueryListener';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import Add from '../../../../Components/Images/Icon_Add_Teal.svg';
import AddDisabled from '../../../../Components/Images/Icon_Add_Grey.svg';
import DeleteDisabled from '../../../../Components/Images/Icon_DeleteDisabled_Grey.svg';
import FileIcon from '../../../../Components/Images/Icon_File_Black.svg';
import Info from '../../../../Components/Images/Icon_Info_Blue.svg';
import ExternalLink from '../../../../Components/Images/Icon_ExternalLink_Blue.svg';

// CSS
import '../../Task.css'

export default function ExecuteTab({
    task
}) {

    //------------------------------------------------------
    // Constants
    //------------------------------------------------------

    const isExecutionNotRequired = task?.executionnotrequired === true;

    //------------------------------------------------------
    //  useContext & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const navigate = useNavigate();
    const routerUseLocation = useLocation();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to toggle the visibility of the add contract pane
    const [addContractPaneOpen, setAddContractPaneOpen] = useState(undefined);

    // Used to toggle the visibility of the view contract pane
    const [viewContractPane, setViewContractPane] = useState(undefined);

    // Used to toggle the visibility of the delete contract pane
    const [deleteContractPane, setDeleteContractPane] = useState(undefined);

    // Used to store the details of agreement that has been published to Inventory
    const [agreementDetails, setAgreementDetails] = useState(undefined);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'signedContractsCollapsed': false,
            'submissionErrors': ''
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to proceed to next stage
    function moveToNextStage() {

        const docPromises = [];

        //=================================================
        //  Update Agreement Document in Firestore
        //=================================================

        const updatedAgreementDoc = {
            'signedcontracts': task?.signedcontracts,
            'executiondate': task?.agreement?.executiondate,
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
        };

        docPromises.push(
            WriteDocument('agreements', task?.agreement?.agreementid, updatedAgreementDoc, true)
        );

        //=================================================
        //  Update Task Document in Firestore
        //=================================================

        const updatedDoc = {
            'stage': 'Raise PO',
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
            'potask': {
                'commercialscope': `Raise PO for ${task?.applicationname}`,
                'shortdescription': `Raise PO for ${task?.applicationname}`
            }
        };

        // Add the updated agreement document to the task document
        docPromises.push(
            WriteDocument('requests', task?.requestid, updatedDoc, true)
        );

        Promise.all(docPromises)
            .then(() => {

                // Navigate to Raise Po Tab
                navigate(`/${routerUseLocation.pathname.split('/')[1]}/${task?.requestid}?view=raisepo`);

            })
            .catch((error) => {

                console.log('Error', error);

            });

    }


    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Document Listener
    // - Get the agreement document if it has been published to Inventory
    useEffect(() => {

        if (task === undefined) return;
        if (task?.poanumber === undefined) return;

        function onLoadChange(document) {

            if (document?.length > 0) {
                setAgreementDetails(document[0]);
            }

        }

        function onError(error) {

            console.log('error', error);

        }

        const unsubscribe = QueryListener('agreements', [['poanumber', '==', task?.poanumber]], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

    }, [task])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0'>

            {/* ===================================================== */}
            {/*  Execute Tab Bypass Status                            */}
            {/* ===================================================== */}

            {
                isExecutionNotRequired && <div className='border-l-[5px] border-gray-500 bg-gray-100 p-3 shadow-sm mb-4'>
                    <p className='text-base font-semibold mb-1'>Execution not required</p>
                    <p className='text-sm mb-0'>
                        <span className='font-semibold'>{task?.lastmodifiedby?.givenname} {task?.lastmodifiedby?.surname}</span> has bypassed the execution of this digital request on
                        <span> {ConvertDate(task?.lastmodifieddate)}</span>
                    </p>
                </div>
            }

            {/* ===================================================== */}
            {/*  Signed Contracts                                     */}
            {/* ===================================================== */}

            <div className='Task-Section-Container'>

                <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'signedContractsCollapsed': !taskSection.signedContractsCollapsed })}>
                    <img
                        className='cursor-pointer'
                        src={taskSection?.signedContractsCollapsed ? Collapse : Expand}
                        alt={'>'}
                    ></img>
                    <h4 className='text-[20px] m-0'>Signed Contracts</h4>
                </div>
                <hr className='m-0' hidden={taskSection?.signedContractsCollapsed}></hr>

                {/* ------------- PUBLISHED TO INVENTORY LABEL ------------- */}

                <div className='flex flex-row gap-3 px-[15px] py-[12px] mx-[3%] mt-[18px] mb-0 text-[15px] rounded-md bg-[#E9F7FF] border-1 border-[#0972D3]' hidden={agreementDetails === undefined || agreementDetails?.signedcontracts?.length === 0}>
                    <img src={Info} alt='info'></img>
                    <p className='mb-0 leading-6'>
                        <b className='font-medium'>{task?.assignedto?.givenname} {task?.assignedto?.surname}</b> has added these contracts into Inventory on {ConvertDate(agreementDetails?.lastmodifieddate)}.
                        <br></br>
                        <a
                            className='flex flex-row gap-2 items-center '
                            href={
                                process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=contracts`
                                    : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=contracts`
                                        : `https://inventory.digitalservices.lendlease.com/inventory/agreement/${agreementDetails?.agreementid}?view=contracts`}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            Make changes to contracts in Inventory
                            <img src={ExternalLink} alt='link'></img>
                        </a>
                    </p>
                </div>

                {/* ------------- SIGNED CONTRACTS ------------- */}

                <div className='p-[3%]' hidden={taskSection?.signedContractsCollapsed}>

                    {/*  Contracts Table */}
                    <div className='border border-[#D8D8D8] rounded px-[1%] overflow-x-auto'>
                        <table className='w-full max-h-96 overflow-y-scroll'>
                            <thead>
                                <tr className='text-[#424242]'>
                                    <th className='py-[15px] px-[5px] font-medium'></th>
                                    <th className='py-[15px] px-[10px] font-medium'>ID</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Signees</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Short Description</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Document</th>
                                    <th className='py-[15px] px-[10px] font-medium'>Created By</th>
                                    <th className='py-[15px] px-[15px] font-medium place-items-end'>
                                        <Tooltip
                                            message={'+ Add Contract'}
                                            children={
                                                <img
                                                    className='cursor-pointer min-w-[20px]'
                                                    src={task?.stage !== 'Execute' || task?.status !== 'In Progress' || isExecutionNotRequired ? AddDisabled : Add}
                                                    alt='add'
                                                    onClick={() => task?.stage !== 'Execute' || task?.status !== 'In Progress' || isExecutionNotRequired ? null : setAddContractPaneOpen(task)}
                                                ></img>
                                            }
                                        ></Tooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    task?.signedcontracts?.length === 0 || task?.signedcontracts === undefined ?
                                        (
                                            <tr>
                                                <td className='border-t border-t-[#D8D8D8]' colSpan={7}>
                                                    <div className='text-center p-[2%]'>
                                                        No contracts found.
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            task?.signedcontracts?.map((contract, index) => (
                                                <tr key={index}>

                                                    <td className='py-[10px] px-[5px] border-t border-t-[#D8D8D8]'></td>

                                                    {/* ID */}
                                                    <td className='py-[10px] px-[10px] border-t whitespace-nowrap overflow-hidden text-ellipsis border-t-[#D8D8D8] break-all uppercase cursor-pointer underline font-medium' onClick={() => setViewContractPane(contract)}>
                                                        {contract?.contractid}
                                                    </td>

                                                    {/* Signees */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            contract?.signees.map((signee, index) => (
                                                                <label key={index} className='max-w-[250px] whitespace-nowrap text-[#00A7A4] bg-[#ECFCFC] font-medium underline px-[10px] py-[6px] rounded-md overflow-hidden text-ellipsis w-fit'>
                                                                    {signee?.signeeemail}
                                                                </label>
                                                            ))
                                                        }
                                                    </td>

                                                    {/* Short Description */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        <div className='leading-normal'>
                                                            {contract?.shortdescription?.length > 0 ? contract?.shortdescription : '-'}
                                                        </div>
                                                    </td>

                                                    {/* Document */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8]'>
                                                        {
                                                            contract?.documents?.length > 0 &&
                                                            <div className='flex flex-row gap-2 pt-2'>
                                                                {
                                                                    contract?.documents?.map((file, index) => (
                                                                        <div key={index} className='flex flex-row gap-2 items-center bg-[#F5F5F5] px-[10px] py-2 rounded-lg'>
                                                                            <img src={FileIcon} alt='file'></img>
                                                                            <a className='max-w-[150px] no-underline font-semibold whitespace-nowrap overflow-hidden text-ellipsis' href={file.fileUrl} target='_blank' rel='noreferrer'>{file.fileName}</a>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        }
                                                    </td>

                                                    {/* Created By */}
                                                    <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] capitalize whitespace-nowrap overflow-hidden text-ellipsis '>
                                                        {contract?.createdby?.givenname} {contract?.createdby?.surname}
                                                    </td>

                                                    {/* Cancel */}
                                                    <td className='py-[10px] px-[15px] border-t border-t-[#D8D8D8] cursor-pointer align-middle place-items-end'>
                                                        {
                                                            (task?.stage !== 'Execute' || task?.status !== 'In Progress' || isExecutionNotRequired) ?
                                                                <img className='cursor-not-allowed' src={DeleteDisabled} alt='cancel'></img>
                                                                :
                                                                <div className='Cancel-Icon mt-2 p-0' onClick={() => setDeleteContractPane(contract)}></div>
                                                        }
                                                    </td>

                                                </tr>
                                            ))
                                        )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ===================================================== */}
            {/*  Move to Next Stage Button                            */}
            {/* ===================================================== */}

            <div className='flex flex-col gap-1 mt-4 mb-0' hidden={isExecutionNotRequired}>

                <button
                    className='Primary-Button w-fit'
                    onClick={() => moveToNextStage()}
                    disabled={task?.stage !== 'Execute' || task?.signedcontracts?.length === 0}
                >
                    Proceed to Next Stage
                </button>

                <p className='font-light italic' hidden={task?.requesttype === 'New Initiative or Technology'}>
                    Signed contracts must be added before proceeding to the next stage.
                </p>

            </div>
            {/* ===================================================== */}
            {/*  Add Contract Pane                                    */}
            {/* ===================================================== */}

            <AddContractPane
                addContractPaneOpen={addContractPaneOpen}
                setAddContractPaneOpen={setAddContractPaneOpen}
            ></AddContractPane>


            {/* ===================================================== */}
            {/*  View Contract Pane                                   */}
            {/* ===================================================== */}

            <ViewContractPane
                viewContractPane={viewContractPane}
                setViewContractPane={setViewContractPane}
            ></ViewContractPane>


            {/* ===================================================== */}
            {/*  Delete Contract Modal                                */}
            {/* ===================================================== */}

            <DeleteContractModal
                task={task}
                deleteContractPane={deleteContractPane}
                setDeleteContractPane={setDeleteContractPane}
            ></DeleteContractModal>

        </div>
    )
}
